import React, { useEffect, useState, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import {
  fetchAccessRequests,
  respondToAccessRequest,
  fetchProjectDetail,
  fetchProjectResources,
  fetchProjectCollaborators,
} from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Button from '../shared/TailwindComponents/Button';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import ProfileImage from '../Header/ProfileImage';
import {
  PiHandPalmLight,
  PiCheckCircleLight,
} from 'react-icons/pi';
import { formatDateTime } from '../../utils/functions';
import { normalise } from '../shared/access/accessModel';

interface PendingAccessRequestsProps {
  resourceType: string;
  resourceUuid: string;
  projectUuid?: string;
}

const PendingAccessRequests: React.FC<PendingAccessRequestsProps> = ({ resourceType, resourceUuid, projectUuid }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [requests, setRequests] = useState<any[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadRequests = useCallback(async () => {
    if (!resourceType || !resourceUuid) return;
    const result = await dispatch(fetchAccessRequests(resourceType, resourceUuid));
    if (result && Array.isArray(result)) {
      setRequests(result);
    }
    setIsLoaded(true);
  }, [dispatch, resourceType, resourceUuid]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleOpenModal = () => {
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Pending Access Requests'),
      subMsg: t('Users requesting access to this resource from linked projects.'),
      width: '520px',
      content: (
        <AccessRequestsModalContent
          requests={requests}
          onRespond={async (requestId, action, accessLevel, tiedToProject) => {
            await dispatch(respondToAccessRequest(requestId, action, accessLevel, tiedToProject));
            loadRequests();
            if (projectUuid) {
              dispatch(fetchProjectDetail(projectUuid));
              dispatch(fetchProjectResources(projectUuid));
              dispatch(fetchProjectCollaborators(projectUuid));
            }
            dispatch(hideModal());
          }}
          onClose={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
    }));
  };

  if (!isLoaded || requests.length === 0) return null;

  return (
    <button
      onClick={handleOpenModal}
      className="relative flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300 hover:bg-warning-100 dark:hover:bg-warning-900/30 transition-colors"
    >
      <PiHandPalmLight className="w-3.5 h-3.5" />
      {requests.length} {t('access request', { count: requests.length })}
      {requests.length > 0 && (
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-warning-500 rounded-full" />
      )}
    </button>
  );
};

// ─── Modal Content ─────────────────────────────────────────────

interface AccessRequestsModalContentProps {
  requests: any[];
  onRespond: (
    requestId: number,
    action: 'approve' | 'deny',
    accessLevel: string,
    tiedToProject: boolean,
  ) => void;
  onClose: () => void;
}

const AccessRequestsModalContent: React.FC<AccessRequestsModalContentProps> = ({ requests, onRespond, onClose }) => {
  const { t } = useTranslation();
  const [accessLevels, setAccessLevels] = useState<Record<number, string>>({});
  // "Tie to project" defaults ON for every request - the legally correct
  // default (grant auto-revokes if the user leaves the Project). Admins can
  // opt out per-request to issue a permanent direct grant instead.
  const [tiedToProject, setTiedToProject] = useState<Record<number, boolean>>({});
  const [processingId, setProcessingId] = useState<number | null>(null);

  const isTied = (reqId: number) => tiedToProject[reqId] !== false; // default true

  const handleApprove = async (requestId: number) => {
    setProcessingId(requestId);
    await onRespond(
      requestId,
      'approve',
      accessLevels[requestId] || 'view',
      isTied(requestId),
    );
    setProcessingId(null);
  };

  const handleDeny = async (requestId: number) => {
    setProcessingId(requestId);
    await onRespond(requestId, 'deny', 'view', true);
    setProcessingId(null);
  };

  return (
    <div className="px-5 pb-5 pt-2">
      <div className="space-y-2.5 max-h-80 overflow-y-auto custom-scrollbar-main">
        {requests.map((req) => (
          <div
            key={req.id}
            className="glass rounded-lg overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <ProfileImage
                profilePicture={normalise(req).avatar}
                email={req.requester_email || ''}
                initials={req.requester_name?.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '??'}
                name={req.requester_name || 'Unknown'}
                size="w-8 h-8"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-white block">{req.requester_name}</span>
                <span className="text-[11px] text-gray-400 dark:text-gray-500">{req.requester_email}</span>
              </div>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{formatDateTime(req.created_at)}</span>
            </div>
            {req.message && (
              <div className="px-4 pb-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 bg-surface-sunken/50 rounded px-2.5 py-1.5 italic">"{req.message}"</p>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-surface-sunken/80 border-t border-hairline-soft">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{t('Grant')}:</span>
                <button
                  onClick={() => setAccessLevels((prev) => ({ ...prev, [req.id]: 'view' }))}
                  className={`px-2 py-0.5 text-[11px] rounded-md transition-colors ${(accessLevels[req.id] || 'view') === 'view' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-500 hover:bg-surface-sunken'}`}
                >
                  {t('View')}
                </button>
                <button
                  onClick={() => setAccessLevels((prev) => ({ ...prev, [req.id]: 'edit' }))}
                  className={`px-2 py-0.5 text-[11px] rounded-md transition-colors ${accessLevels[req.id] === 'edit' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-500 hover:bg-surface-sunken'}`}
                >
                  {t('Edit')}
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="neutral"
                  size="small"
                  onClick={() => handleDeny(req.id)}
                  disabled={processingId === req.id}
                >
                  {t('Deny')}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => handleApprove(req.id)}
                  disabled={processingId === req.id}
                >
                  <PiCheckCircleLight className="w-3.5 h-3.5 mr-0.5" />
                  {t('Approve')}
                </Button>
              </div>
            </div>
            {/* Tie-to-project toggle - defaults ON per the legal-correct model.
                Unchecking gives the requester a permanent direct grant that
                survives Project membership / link changes. */}
            <div
              className="flex items-start gap-2 px-4 py-2 border-t border-hairline-soft select-none"
              title={t('Keep grant tied to the project so it auto-revokes on leave/unlink')}
            >
              <CheckBox
                label={t('Tie this grant to the project')}
                hideLabel
                translate={false}
                checked={isTied(req.id)}
                onChange={(e) => setTiedToProject((prev) => ({ ...prev, [req.id]: e.target.checked }))}
              />
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                {t('Tie this grant to the project')}
                <span className="block text-gray-400 dark:text-gray-500">
                  {isTied(req.id)
                    ? t('Access will be auto-revoked if the user leaves the project or this resource is unlinked.')
                    : t('Grant will be permanent (direct share). Survives project changes.')}
                </span>
              </span>
            </div>
          </div>
        ))}
      </div>
      {requests.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">{t('No pending requests')}</p>
      )}
    </div>
  );
};

export default PendingAccessRequests;
